import React, { useEffect, useRef, useState } from 'react';
import { X, Check } from 'lucide-react';
import gsap from 'gsap';

const BookingModal = ({ isOpen, onClose, selectedService, services }) => {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Update selected service when prop changes
  useEffect(() => {
    if (selectedService) {
      setFormData(prev => ({ ...prev, service: selectedService }));
      // Clear errors when service changes or modal opens
      setErrors({}); 
    }
  }, [selectedService]);

  // Animation for Open/Close
  useEffect(() => {
    if (isOpen) {
      // Clear errors on open
      setErrors({});
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      const tl = gsap.timeline();
      
      tl.to(overlayRef.current, {
        opacity: 1,
        duration: 0.3,
        display: 'flex',
        ease: 'power2.out'
      })
      .fromTo(contentRef.current,
        { y: 50, opacity: 0, scale: 0.95 },
        { y: 0, opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.2)' },
        "-=0.2"
      );
      
    } else {
      document.body.style.overflow = 'auto';
      
      const tl = gsap.timeline({
        onComplete: () => {
          if (overlayRef.current) overlayRef.current.style.display = 'none';
          // Reset success state on close after animation
          setTimeout(() => setIsSuccess(false), 200); 
        }
      });
      
      tl.to(contentRef.current, {
        y: 20,
        opacity: 0,
        scale: 0.95,
        duration: 0.3,
        ease: 'power2.in'
      })
      .to(overlayRef.current, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in'
      }, "-=0.2");
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.address.trim()) newErrors.address = 'Property details are required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const endpoint = `${API_URL}/api/booking`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        // Reset form data after success
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          service: '',
          message: ''
        });
      } else {
        alert(data.message || "Failed to submit booking. Please try again.");
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert("Failed to send booking request. Please check if the backend server is running.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen && !overlayRef.current) return null; // Initial render safe check

  return (
    <div 
      ref={overlayRef} 
      className={`fixed inset-0 z-[100] ${isOpen ? 'flex' : 'hidden'} opacity-0 items-center justify-center p-4 md:p-6`}
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-forest-green/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div 
        ref={contentRef}
        className="relative w-full max-w-2xl bg-[#FAF9F6] rounded-2xl shadow-2xl overflow-hidden border border-white/40 flex flex-col max-h-[90vh]"
      >
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-sage-mist/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-soft-clay/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-forest-green/60 hover:text-forest-green hover:bg-black/5 rounded-full transition-colors z-10"
          >
            <X size={24} />
          </button>

          <div className="relative overflow-y-auto flex-grow p-8 md:p-10 custom-scrollbar">
            {!isSuccess ? (
              <>
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-serif text-forest-green mb-2">Begin Your Journey</h3>
                  <p className="text-gray-600">Share your details below to schedule your land healing session.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 max-w-xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-forest-green ml-1">Full Name <span className="text-red-500">*</span></label>
                      <input 
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl bg-white border ${errors.name ? 'border-red-500 focus:border-red-500' : 'border-stone-200 focus:border-sage-mist'} focus:ring-2 focus:ring-sage-mist/20 outline-none transition-all placeholder:text-gray-300`}
                        placeholder="e.g. Amelia Downie"
                      />
                      {errors.name && <p className="text-red-500 text-xs ml-1">{errors.name}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-forest-green ml-1">Email Address <span className="text-red-500">*</span></label>
                      <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl bg-white border ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-stone-200 focus:border-sage-mist'} focus:ring-2 focus:ring-sage-mist/20 outline-none transition-all placeholder:text-gray-300`}
                        placeholder="you@example.com"
                      />
                      {errors.email && <p className="text-red-500 text-xs ml-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-forest-green ml-1">Phone Number</label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-stone-200 focus:border-sage-mist focus:ring-2 focus:ring-sage-mist/20 outline-none transition-all placeholder:text-gray-300"
                        placeholder="+61 400 000 000"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-forest-green ml-1">Interested Service</label>
                      <div className="relative">
                        <select 
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-white border border-stone-200 focus:border-sage-mist focus:ring-2 focus:ring-sage-mist/20 outline-none transition-all appearance-none cursor-pointer"
                        >
                          <option value="" disabled>Select a plan...</option>
                          {services.map((s, i) => (
                            <option key={i} value={s.title}>{s.title} ({s.price})</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-forest-green ml-1">Property Address / Details <span className="text-red-500">*</span></label>
                    <textarea 
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows="3"
                      className={`w-full px-4 py-3 rounded-xl bg-white border ${errors.address ? 'border-red-500 focus:border-red-500' : 'border-stone-200 focus:border-sage-mist'} focus:ring-2 focus:ring-sage-mist/20 outline-none transition-all placeholder:text-gray-300 resize-none`}
                      placeholder="Please provide the specific address you need scanned..."
                    ></textarea>
                    {errors.address && <p className="text-red-500 text-xs ml-1">{errors.address}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-forest-green ml-1">Additional Message (Optional)</label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-stone-200 focus:border-sage-mist focus:ring-2 focus:ring-sage-mist/20 outline-none transition-all placeholder:text-gray-300 resize-none"
                      placeholder="Any specific concerns or questions?"
                    ></textarea>
                  </div>

                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 mt-4 rounded-xl font-medium tracking-wide bg-forest-green text-earthy-sand hover:bg-[#2A4432] transition-colors relative overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed interactive"
                  >
                    <span className={`relative z-10 flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-0' : 'opacity-100'}`}>
                      Request Booking
                    </span>
                    {isSubmitting && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      </div>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6 animate-bounce-slow">
                  <Check size={40} strokeWidth={3} />
                </div>
                <h3 className="text-3xl font-serif text-forest-green mb-3">Request Received</h3>
                <p className="text-gray-600 max-w-md mx-auto mb-8">
                  Thank you, {formData.name}. I have received your request for {formData.service}. I will be in touch shortly to confirm the details.
                </p>
                <button 
                  onClick={onClose}
                  className="px-8 py-3 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-forest-green transition-colors interactive"
                >
                  Close
                </button>
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default BookingModal;
